import { useState } from 'react';

interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
  mockStatus: 'pending' | 'done' | 'na';
}

const DEFAULT_CHECKLIST: ChecklistItem[] = [
  { id: 'id-verify', label: '身份已核實（證件查驗）', required: true, mockStatus: 'pending' },
  { id: 'physical-id', label: '實體證件已收取影本', required: true, mockStatus: 'pending' },
  { id: 'equipment-check', label: '器材清點與功能檢查完成', required: true, mockStatus: 'pending' },
  { id: 'promissory-note', label: '本票 / 擔保文件已簽署', required: true, mockStatus: 'pending' },
  { id: 'customer-sign', label: '客戶簽名確認（合約/交接單）', required: true, mockStatus: 'pending' },
  { id: 'early-pickup', label: '提前取件核准（如適用）', required: false, mockStatus: 'na' },
];

interface Props {
  visible: boolean;
  onToggleItem?: (id: string) => void;
}

export default function PickupGovernanceChecklist({ visible, onToggleItem }: Props) {
  const [items, setItems] = useState<ChecklistItem[]>(DEFAULT_CHECKLIST);

  if (!visible) return null;

  const handleToggle = (id: string) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const nextStatus = item.mockStatus === 'done' ? 'pending' : 'done';
        return { ...item, mockStatus: nextStatus };
      }),
    );
    onToggleItem?.(id);
  };

  const completedCount = items.filter((i) => i.mockStatus === 'done').length;
  const requiredCount = items.filter((i) => i.required).length;
  const requiredDone = items.filter((i) => i.required && i.mockStatus === 'done').length;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <i className="ri-file-list-3-line text-teal-600"></i>
          取件前治理檢核
        </h2>
        <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
          Mock / Pending backend
        </span>
      </div>

      <div className="mb-3 text-sm text-gray-500">
        必填項目已完成 {requiredDone} / {requiredCount}
        <span className="text-xs text-gray-400 ml-2">（此清單僅供前端參考，正式流程待後端接線）</span>
      </div>

      <div className="space-y-2">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => handleToggle(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors cursor-pointer ${
              item.mockStatus === 'done'
                ? 'bg-teal-50 border-teal-200'
                : item.mockStatus === 'na'
                  ? 'bg-gray-50 border-gray-200 opacity-60'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                item.mockStatus === 'done'
                  ? 'bg-teal-500 text-white'
                  : item.mockStatus === 'na'
                    ? 'bg-gray-300 text-white'
                    : 'border-2 border-gray-300'
              }`}
            >
              {item.mockStatus === 'done' && <i className="ri-check-line text-sm"></i>}
              {item.mockStatus === 'na' && <i className="ri-subtract-line text-sm"></i>}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">{item.label}</div>
            </div>
            {item.required && (
              <span className="text-xs text-red-500 font-medium">必填</span>
            )}
            {item.mockStatus === 'na' && (
              <span className="text-xs text-gray-400">不適用</span>
            )}
          </button>
        ))}
      </div>

      {completedCount === items.length && (
        <div className="mt-4 p-3 bg-teal-50 rounded-lg border border-teal-200">
          <div className="flex items-center gap-2 text-sm text-teal-700">
            <i className="ri-checkbox-circle-fill text-teal-600"></i>
            所有檢核項目已完成，可執行取件放行
          </div>
        </div>
      )}
    </div>
  );
}